import React, { ReactElement, useEffect, useState } from 'react';
import {
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  makeStyles,
  createStyles,
  CircularProgress,
  CardHeader,
  DefaultButton,
  Tooltip,
  IconButton,
  Typography,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { UseRestApiResult } from 'hooks/useRestApi.types';
import { DdaUserData } from 'App.types';
import { useSnackbar } from 'hooks/useSnackbar';
import { Information } from '@bb-ui/icons/dist/small';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { useTenantContext } from 'contexts/TenantContext';

interface DdaUsersResp extends Omit<UseRestApiResult, 'data'> {
  data: {
    items: DdaUserData[];
  };
}

interface DdaUserUIProps extends DdaUserData {
  secret?: string;
}

export const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
    },
    tableHead: {
      height: '100%',
    },
    idCell: {
      width: '50%',
    },
    userNameCell: {
      width: '30%',
    },
    passwordCell: {
      width: '20%',
    },
    heading: {
      marginBottom: '14px',
    },
    section: {
      marginTop: '24px',
    },
  }),
);

export const DdaDefinitionsUsers: React.FC = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useTenantContext();
  const { learnInstanceId } = useLearnInstance();
  const tenantId = tenant?.tenantType === 'Learn' ? learnInstanceId : tenant?.id;
  const [userName, setUserName] = useState<string | null>('');
  const [ddaUsers, setDdaUsers] = useState<{ items: DdaUserData[] }>({ items: [] });
  const [isActionBtnDisabled, setActionBtnDisabled] = useState<boolean>(false);

  const { data, error, loading, fetch } = useRestApi(
    apiUrl('lct', `dda/tenants/${tenantId}/users`),
    { manual: true },
  ) as DdaUsersResp;

  const emailNotificactionUrl = apiUrl('lct', `dda/tenants/${tenantId}/secrets/${userName}`);

  const {
    succeededRequests: emailSucceededRequests,
    failedRequests: emailFailedRequests,
    clearSucceededRequests,
    clearFailedRequests,
    doPost: triggerEmailNotification,
  } = useRestApi(emailNotificactionUrl, {
    manual: true,
  });

  let content: ReactElement;

  useEffect(() => {
    if (tenantId) {
      fetch();
    }
  }, [fetch, tenantId]);

  useEffect(() => {
    setDdaUsers(data);
  }, [data]);

  useEffect(() => {
    if (userName) {
      triggerEmailNotification({});
    }
  }, [userName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleEmailResponse();
  }, [emailSucceededRequests, emailFailedRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  const loader = () => (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-dda-user"
    />
  );

  const erroMessage = () => (
    <Typography data-testid="dda-user-info-error">{t('global.fetchError')}</Typography>
  );

  const noDdaUserMessage = () =>
    (content = <Typography data-testid="dda-user-list-no-data">{t('ddaList.noData')}</Typography>);

  /**
   * Handle email response in notification to the user
   */
  const handleEmailResponse = (): void => {
    if (emailSucceededRequests?.length) {
      enqueueSnackbar(t('ddaUser.emailSuccess'), {
        variant: 'success',
      });
      clearSucceededRequests();
    }

    if (emailFailedRequests?.length) {
      enqueueSnackbar(t('ddaUser.emailError'), {
        variant: 'error',
      });
      clearFailedRequests();
    }

    setUserName(null);
    setActionBtnDisabled(false);
  };

  /**
   * when user click send credential button to trigger email notification
   * @param userName - dda user name
   */
  const onClickSendCredential = (userName: string): void => {
    setUserName(userName);
    setActionBtnDisabled(true);
  };

  const userTable = (ddaUser: DdaUserUIProps[]) => (
    <Table className={classes.table} data-testid="dda-user-datatable">
      <TableHead>
        <TableRow className={classes.tableHead}>
          <TableCell className={classes.idCell}>{t('ddaUser.userId')}</TableCell>
          <TableCell className={classes.userNameCell}>{t('ddaUser.userName')}</TableCell>
          <TableCell className={classes.passwordCell}>
            {t('ddaUser.credential')}
            <Tooltip
              title={t('ddaUser.toolTipTitle') ?? ''}
              data-testid="dda-user-credential-tooltip"
            >
              <IconButton>
                <Information />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {ddaUser.map((user: DdaUserUIProps) => (
          <TableRow key={user.id}>
            <TableCell>{user.id}</TableCell>
            <TableCell>{user.userName}</TableCell>
            <TableCell align="left">
              <DefaultButton
                data-testid="dda-user-credential-btn"
                onClick={() => onClickSendCredential(user.userName)}
                disabled={isActionBtnDisabled}
              >
                Send Credential
              </DefaultButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (!tenantId) {
    content = loader();
  } else if (error) {
    content = erroMessage();
  } else if (loading) {
    content = loader();
  } else if (!ddaUsers?.items.length) {
    content = noDdaUserMessage();
  } else {
    content = userTable(ddaUsers.items);
  }

  return (
    <div data-testid="dda-users-section" className={classes.section}>
      <CardHeader
        title={t('ddaUser.pageTitle')}
        titleTypographyProps={{
          color: 'textSecondary',
          component: 'h3' as any,
          variant: 'h4',
        }}
        className={classes.heading}
      />
      {content}
    </div>
  );
};
